/**
 * templates.ts — plain-English sentence builder for the chemistry coach.
 *
 * Templates are data, not prose-in-code. Each (status, trend) combination
 * has a distinct template so output is deterministic and fully testable.
 */
import type { CoachParameter } from './ranges';

// ---------------------------------------------------------------------------
// Context passed to buildSentence
// ---------------------------------------------------------------------------

export interface SentenceContext {
  parameter: CoachParameter;
  value: number;
  unit: string;
  status: 'in-range' | 'above' | 'below';
  trend: 'up' | 'down' | 'flat';
  perDayRate: number;
  windowDays: number;
  range: { min: number; max: number };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const DISPLAY_NAMES: Record<CoachParameter, string> = {
  alk: 'alkalinity',
  ca: 'calcium',
  mg: 'magnesium',
  pH: 'pH',
  salinity: 'salinity',
  nitrate: 'nitrate',
  phosphate: 'phosphate',
};

function fmtValue(v: number): string {
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

function fmtRate(r: number): string {
  const abs = Math.abs(r);
  if (abs >= 1) return r.toFixed(1);
  if (abs < 0.001) return r.toFixed(4);
  return r.toFixed(2);
}

function unitSuffix(unit: string): string {
  return unit ? ` ${unit}` : '';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build one plain-English coaching sentence from the full SentenceContext.
 */
export function buildSentence(ctx: SentenceContext): string {
  const { parameter, value, unit, status, trend, perDayRate, range } = ctx;
  const name = DISPLAY_NAMES[parameter];
  const v = fmtValue(value);
  const u = unitSuffix(unit);
  const rate = fmtRate(Math.abs(perDayRate));
  const sign = perDayRate >= 0 ? '+' : '−';
  const rangeStr = `${fmtValue(range.min)}–${fmtValue(range.max)}${u}`;

  if (status === 'in-range') {
    if (trend === 'flat') return `${name} ${v}${u} — in range, holding steady`;
    if (trend === 'up')   return `${name} ${v}${u} — in range, trending up ${sign}${rate}/day`;
    /* down */            return `${name} ${v}${u} — in range, trending down −${rate}/day`;
  }

  if (status === 'above') {
    if (trend === 'flat') return `${name} ${v}${u} — above range (${rangeStr}), stable`;
    if (trend === 'up')   return `${name} ${v}${u} — above range and rising +${rate}/day`;
    /* down */            return `${name} ${v}${u} — above range, drifting down toward target −${rate}/day`;
  }

  // below
  if (trend === 'flat') return `${name} ${v}${u} — below range (${rangeStr}), stable`;
  if (trend === 'up')   return `${name} ${v}${u} — below range, improving +${rate}/day`;
  /* down */            return `${name} ${v}${u} — below range and falling −${rate}/day`;
}
